import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { Icon } from 'semantic-ui-react';
import { Route, Switch, Link, match } from 'react-router-dom';
import { RouteChildrenProps } from 'react-router';
import { includes, pathOr } from 'ramda';

import styles from './SubHeader.styles';

import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';

import MerchantVenueSearch from 'scenes/Home/components/MerchantVenueSearch';
import DatePicker from 'components/DatePicker';
import Splitio from 'components/Splitio';
import FilterButton from 'components/Header/components/Filters/components/FilterButton';
import PlacesServiceSearch from 'scenes/Home/scenes/MerchantVenuesOnboarding/components/PlacesServiceSearch';

import Budgets from './components/Budgets';
import DownloadData from './components/DownloadData';
import MerchantLogo from './components/MerchantLogo';

type Props = {
  classes: ClassProp<typeof styles>;
};

type Params = {
  history: History;
  location: Location;
  match: match;
};

const HomepageVenueSearch = () => (
  <MerchantVenueSearch
    path="/venues/search"
    requireSearch
    showFilters={false}
    showVMID
    isInternalUser
    isHomeView={true}
  />
);

const VenueSearchResults = ({ match }: Params) => (
  <MerchantVenueSearch
    path={match.url}
    requireSearch={false}
    showFilters
    showVMID={false}
    isInternalUser
  />
);

const VenueDiscoveredSearchResults = ({ match }: Params) => (
  <MerchantVenueSearch
    path={match.url}
    requireSearch={false}
    showFilters={false}
    showVMID
    showOnlyVMID
    isInternalUser
  />
);

const showDatePicker = (pathname: string) => !includes('offer', pathname);

const MerchantDetails = (
  routeProps: RouteChildrenProps,
  classes: ClassProp<typeof styles>,
  isInternalUser: boolean,
  isExternalUser: boolean,
  isTier3: boolean,
  isDemoUser: boolean,
) => {
  const {
    location: { pathname },
  } = routeProps;

  return (
    <div className={classes.merchantDetails}>
      {isInternalUser && (
        <Link to="/">
          <Icon name="arrow alternate circle left outline" />
          Back to All Merchants
        </Link>
      )}

      {isExternalUser && <MerchantLogo />}

      <div>
        <Splitio feature="budget_widget">
          <Budgets />
        </Splitio>
        {showDatePicker(pathname) && <DatePicker />}
      </div>
      {!isTier3 && <FilterButton />}
      {!isDemoUser && <DownloadData />}
    </div>
  );
};

const DateRange = (
  _: RouteChildrenProps,
  classes: ClassProp<typeof styles>,
) => (
  <div className={classes.datePicker}>
    <DatePicker />
  </div>
);

const ReportReturnCTA = (
  routeProps: RouteChildrenProps,
  classes: ClassProp<typeof styles>,
  isDemoUser: boolean,
) => {
  let path = '/reports';
  if (isDemoUser) {
    const merchantName = pathOr(
      '',
      ['match', 'params', 'merchantName'],
      routeProps,
    );

    path = `/${merchantName}/reports`;
  }

  return (
    <div className={classes.reportReturnCTA}>
      <Link to={path}>
        <Icon name="arrow alternate circle left outline" />
        Back to Reports
      </Link>
    </div>
  );
};

// remove / hide the sub header when on the sign up form
const NoSubHeader = () => null;

const SubHeader: FunctionComponent<Props> = ({ classes }) => {
  const { isInternalUser, isExternalUser, isTier3, isDemoUser } = useUserMgmt();
  const renderMerchantDetails = (routeProps: RouteChildrenProps) =>
    MerchantDetails(
      routeProps,
      classes,
      isInternalUser,
      isExternalUser,
      isTier3,
      isDemoUser,
    );

  const renderDatePicker = (routeProps: RouteChildrenProps) =>
    DateRange(routeProps, classes);

  const renderReturnReportsCTA = (routeProps: RouteChildrenProps) =>
    ReportReturnCTA(routeProps, classes, isDemoUser);

  return (
    <Switch>
      <Route exact path="/" component={HomepageVenueSearch} />
      <Route exact path="/venues/search" component={VenueSearchResults} />
      <Route
        exact
        path={['/sign-up', '/account-management', '/create-merchant']}
        component={NoSubHeader}
      />

      <Route
        exact
        path={[
          '/merchant/:merchantId/venues/:merchantName/management',
          '/:merchantName/venues/management',
        ]}
        component={PlacesServiceSearch}
      />

      <Route
        exact
        path="/venue-discovery/search"
        component={VenueDiscoveredSearchResults}
      />

      <Route
        exact
        render={renderDatePicker}
        path={[
          '/:merchantName/reports',
          '/network-health',
          '/venues/search/venue/:venueId',
          '/reports',
        ]}
      />

      <Route
        exact
        render={renderReturnReportsCTA}
        path={[
          '/reports/detail/:reportName',
          '/reports/batch-report',
          '/:merchantName/reports/detail/:reportName',
          '/:merchantName/reports/batch-report',
        ]}
      />

      <Route
        exact
        render={renderMerchantDetails}
        path={[
          '/merchant/:merchantId/*/:merchantName',
          '/:merchantName',
          '/:merchantName/overview',
          '/:merchantName/venues',
          '/:merchantName/lift',
          '/:merchantName/offers',
        ]}
      />
    </Switch>
  );
};

export default withStyles(styles)(SubHeader);
